import {
  hashKey,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { latestWriteStartedAt, shouldRefreshOnOwnEcho } from "./echo-filter";

/**
 * How long realtime refreshes are gathered before one flush. One write often
 * arrives as several events, and one event on several channels a page listens to
 * (an `order.changed` on the order AND the team channel); gathering them refetches
 * each query once instead of cancelling and restarting it per event.
 */
export const REFRESH_COALESCE_MS = 100;

/**
 * How long a write's refreshes are remembered. One write's events can reach a
 * tab on several channels up to a second or two apart (each channel is its own
 * Redis round trip); a query refreshed for that write is not refreshed again for
 * it within this window. Its first refetch started after the write committed (the
 * event is sent after the commit), so it already shows the write.
 */
export const WRITE_DEDUPE_MS = 10_000;

/** What one realtime event says about itself. */
export interface RefreshCause {
  /** The event echoes this tab's own write (its origin is ours). */
  ownEcho: boolean;
  /** The write that caused it; null when the publisher sent none. */
  writeId: string | null;
}

export interface RefreshScheduler {
  /**
   * Queue keys to refresh at the next flush. A key already refreshed for the
   * same write (within {@link WRITE_DEDUPE_MS}) is skipped.
   *
   * @param keys - Query keys (or prefixes) to invalidate
   * @param cause - Whether it echoes this tab's write, and which write caused it
   */
  schedule: (keys: readonly QueryKey[], cause: RefreshCause) => void;
  /** Drop anything queued and stop the timer. */
  dispose: () => void;
}

/**
 * The one realtime refresh queue of a QueryClient.
 *
 * A key queued both as an own echo and from elsewhere refreshes as from elsewhere:
 * someone else's write is news even when this tab also wrote.
 *
 * @param queryClient - The app's QueryClient
 * @param now - Clock, for the per-write memory
 */
export function createRefreshScheduler(
  queryClient: QueryClient,
  now: () => number,
): RefreshScheduler {
  const pending = new Map<string, { key: QueryKey; ownEcho: boolean }>();
  /** `<writeId>|<key hash>` → when that refresh may be repeated for that write. */
  const refreshedForWrite = new Map<string, number>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  /** True the first time a key is refreshed for a write; false for a repeat. */
  const firstForWrite = (writeId: string | null, hash: string): boolean => {
    if (!writeId) return true;
    const at = now();
    for (const [seen, until] of refreshedForWrite) {
      if (until <= at) refreshedForWrite.delete(seen);
    }
    const id = `${writeId}|${hash}`;
    if (refreshedForWrite.has(id)) return false;
    refreshedForWrite.set(id, at + WRITE_DEDUPE_MS);
    return true;
  };

  const flush = () => {
    timer = undefined;
    const entries = [...pending.values()];
    pending.clear();
    const writeStartedAt = latestWriteStartedAt(
      queryClient.getMutationCache().getAll(),
    );
    for (const { key, ownEcho } of entries) {
      void queryClient.invalidateQueries({
        queryKey: key,
        ...(ownEcho
          ? {
              predicate: (query) =>
                shouldRefreshOnOwnEcho(query, writeStartedAt),
            }
          : {}),
      });
    }
  };

  return {
    schedule: (keys, { ownEcho, writeId }) => {
      for (const key of keys) {
        const hash = hashKey(key);
        if (!firstForWrite(writeId, hash)) continue;
        const queued = pending.get(hash);
        pending.set(hash, {
          key,
          ownEcho: ownEcho && (queued?.ownEcho ?? true),
        });
      }
      if (pending.size > 0) timer ??= setTimeout(flush, REFRESH_COALESCE_MS);
    },
    dispose: () => {
      clearTimeout(timer);
      timer = undefined;
      pending.clear();
      refreshedForWrite.clear();
    },
  };
}
